import { useCallback, useEffect, useMemo } from 'react';

import { useSyncExternalStore } from 'use-sync-external-store/shim';

import { DEFAULT_PRODUCT_CONTEXT, type ProductContext } from '../../common/constants';
import {
	AssetsWorkspaceHostStatus,
	type AssetsWorkspaceHostState,
	type UseAssetsWorkspaceHostOptions,
} from '../../common/types';
import { fetchAssetsWorkspaceHost } from '../../services/fetchAssetsWorkspaceHost';
import { getServerSnapshot, getSnapshot, isStale, subscribe } from '../../state/store';

const IDLE_RESULT: AssetsWorkspaceHostState = {
	status: AssetsWorkspaceHostStatus.Idle,
	data: null,
	error: null,
};

const absoluteUrlFn = (path: string): string => path;

export type UseAssetsWorkspaceHostResult = AssetsWorkspaceHostState & {
	readonly isIdle: boolean;
	readonly isPending: boolean;
	readonly isSuccess: boolean;
	readonly refetch: () => void;
	/**
	 * The Units-aware Assets site cloud id when {@link isSuccess} is `true` and
	 * the BE supplied one for this tenant, otherwise `null`. Use the `??`
	 * fallback to swap in the current browsing site's `cloudId` at the call
	 * site when this is `null`.
	 */
	readonly resolvedCmdbSiteCloudId: string | null;
	/**
	 * The resolved workspaceId when {@link isSuccess} is `true`, otherwise `null`.
	 * Use the `??` fallback to swap in a legacy site-local workspaceId at the call site.
	 */
	readonly resolvedWorkspaceId: string | null;
	/**
	 * Absolute-URL builder when {@link isSuccess} is `true`. Returns the input
	 * path unchanged (identity) for every other status, so consumers can always
	 * call it without a guard.
	 */
	readonly toResolvedAbsoluteUrl: (relativePath: string) => string;
};

/**
 * React hook returning the resolved Assets workspace coordinates for the
 * given tenant.
 *
 * `cloudId` is mandatory: read it from `useTenantContext()` (or equivalent)
 * at the call site. The cache key, singleflight scope, and TTL window are
 * all per-`cloudId`, so passing the right tenant is the contract that keeps
 * cross-tenant entries from leaking into each other. The BE returns the same
 * `workspaceId` regardless of `productContext`, so all variants for one
 * `cloudId` share a single cache entry.
 *
 * Optional args:
 *  - `isOnlyWorkspace` (default `false`): when `true`, hits the canonical
 *    `/workspace` endpoint with no product context. When `false`, hits
 *    `/internal/workspace?productContext={productContext}`.
 *  - `productContext` (default `'jsm'`): one of `'jira' | 'jsm' | 'confluence'`,
 *    forwarded as the query parameter on `/internal/workspace`. Ignored when
 *    `isOnlyWorkspace` is `true`.
 *  - `skip` (default `false`): when `true`, the hook returns `idle` and never
 *    subscribes / fetches.
 *
 * SSR-safe: server snapshot is always `pending`, so the slow resolver never
 * blocks server render. The first effect after hydration kicks off the
 * fetch (or reads from the in-memory cache when one is fresh).
 *
 * Cross-component coalesced: every subscriber for the same `cloudId` shares
 * one network call via the module-scoped store.
 */
export const useAssetsWorkspaceHost = (
	options: UseAssetsWorkspaceHostOptions,
): UseAssetsWorkspaceHostResult => {
	const cloudId: string = options.cloudId;
	const isOnlyWorkspace: boolean = options.isOnlyWorkspace === true;
	const productContext: ProductContext = options.productContext ?? DEFAULT_PRODUCT_CONTEXT;
	const skip: boolean = options.skip === true;

	const subscribeForCloudId = useCallback(
		(listener: () => void) => subscribe(cloudId, listener),
		[cloudId],
	);
	const getSnapshotForCloudId = useCallback(() => getSnapshot(cloudId), [cloudId]);

	const state = useSyncExternalStore(subscribeForCloudId, getSnapshotForCloudId, getServerSnapshot);

	useEffect(() => {
		if (skip) {
			return;
		}
		// Trigger fetch on mount, on cloudId change, or when the cached value
		// has gone stale. The fetcher itself is single-flight so multiple
		// hooks won't fan out into multiple network calls.
		//
		// On a cold mount the first paint sees `idle`; the effect synchronously
		// flips the store to `pending` (via `setPending` inside
		// `fetchAssetsWorkspaceHost`) before the network resolves, so the next
		// render reflects in-flight state. Errors land in the store via the
		// fetcher itself; the trailing `.catch` is purely there to avoid an
		// unhandled rejection.
		if (
			state.status === AssetsWorkspaceHostStatus.Idle ||
			(state.status === AssetsWorkspaceHostStatus.Success && isStale(cloudId))
		) {
			void fetchAssetsWorkspaceHost({ cloudId, isOnlyWorkspace, productContext }).catch(() => {
				// no-op — store already reflects the failure
			});
		}
	}, [cloudId, isOnlyWorkspace, productContext, skip, state.status]);

	const refetch = useCallback(() => {
		if (skip) {
			return;
		}
		// `refetch` must never throw — errors are surfaced through the
		// store-driven re-render as `status: 'error' | 'unavailable'`. The
		// IIFE + try/catch guarantees no unhandled rejection escapes into
		// consumers.
		void (async () => {
			try {
				await fetchAssetsWorkspaceHost({
					cloudId,
					force: true,
					isOnlyWorkspace,
					productContext,
				});
			} catch {
				// no-op — store already reflects the failure
			}
		})();
	}, [cloudId, isOnlyWorkspace, productContext, skip]);

	return useMemo<UseAssetsWorkspaceHostResult>(() => {
		const baseState = skip ? IDLE_RESULT : state;
		const isSuccess = baseState.status === AssetsWorkspaceHostStatus.Success;
		const isPending = baseState.status === AssetsWorkspaceHostStatus.Pending;
		const isIdle = baseState.status === AssetsWorkspaceHostStatus.Idle;
		return {
			...baseState,
			refetch,
			isIdle,
			isPending,
			isSuccess,
			resolvedCmdbSiteCloudId: isSuccess ? baseState.data.cmdbSiteCloudId : null,
			resolvedWorkspaceId: isSuccess ? baseState.data.workspaceId : null,
			toResolvedAbsoluteUrl: isSuccess ? baseState.data.toAbsoluteUrl : absoluteUrlFn,
		};
	}, [skip, state, refetch]);
};
